import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, ContentLoader, Input, Modal, Pagination, Select } from '@/components/common';
import { useAuthStore } from '@/store';
import api, { getErrorMessage } from '@/services/api';
import { eventLabel } from './activityLogLabels';
import { formatDate, formatDateTime } from '@/utils';

/**
 * #404: the insurer activity log (append-only, kept 8 years).
 *
 * One page, two doors:
 *  - `/insurer/activity-log` (scope "insurer"): an insurer admin sees their
 *    own insurer's log; an insurer employee sees only their OWN activity and
 *    gets no export or integrity check (the API refuses both).
 *  - `/admin/insurer-activity` (scope "platform"): a country admin picks one
 *    of their country's insurers or "All insurers"; super admin sees all.
 *
 * Read-only by design: there is no edit or delete control anywhere, because
 * the server has no route for either and the database refuses both.
 * Filter state lives in the URL so a reload or a shared link keeps it.
 */

export interface InsurerAuditEvent {
  id: string;
  seq: number;
  occurred_at: string;
  insurer_id: string;
  insurer_name: string;
  event: string;
  category: string;
  outcome: 'success' | 'denied' | 'failed';
  actor_type: string;
  actor_user_id: string | null;
  actor_email: string;
  actor_name: string;
  actor_role: string;
  acting_for_country_admin: boolean;
  object_type: string;
  object_id: string;
  object_label: string;
  summary: string;
}

interface InsurerAuditEventDetail extends InsurerAuditEvent {
  payload: Record<string, unknown>;
  changes: Record<string, { old?: unknown; new?: unknown; changed?: boolean }>;
  request_id: string;
  ip_address: string | null;
  user_agent: string;
  source: string;
  prev_hash: string;
  row_hash: string;
}

interface EventsResponse {
  count: number;
  total_pages: number;
  current_page: number;
  page_size: number;
  results: InsurerAuditEvent[];
}

interface Facets {
  categories: string[];
  events: string[];
  log_started_at: string | null;
  insurers?: { id: string; name: string }[];
}

const BASE = '/audit/insurer-events/';

const CATEGORY_LABELS: Record<string, string> = {
  staff: 'Staff',
  security: 'Sign-in & security',
  underwriting: 'Underwriting',
  policy: 'Policies',
  claims: 'Claims',
  config: 'Configuration',
  commission: 'Commission',
};

const OUTCOME_VARIANT: Record<string, 'success' | 'warning' | 'error'> = {
  success: 'success',
  denied: 'warning',
  failed: 'error',
};

const OUTCOME_OPTIONS = [
  { value: '', label: 'Any outcome' },
  { value: 'success', label: 'Success' },
  { value: 'denied', label: 'Denied' },
  { value: 'failed', label: 'Failed' },
];

const FILTER_KEYS = ['insurer', 'category', 'event', 'outcome', 'actor', 'q', 'date_from', 'date_to'] as const;

function isoDay(date: Date) {
  return date.toISOString().slice(0, 10);
}

function defaultFrom() {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return isoDay(date);
}

function showValue(value: unknown) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function actorLabel(row: InsurerAuditEvent) {
  if (row.actor_type === 'system') return 'System';
  return row.actor_name || row.actor_email || 'Unknown';
}

export function ActivityLogPage({ scope }: { scope: 'insurer' | 'platform' }) {
  const { user } = useAuthStore();
  const ownActivityOnly = user?.role === 'insurer_employee';
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Default view: the last 30 days (the API itself has no default range).
  const filters = useMemo(() => {
    const values: Record<string, string> = {};
    for (const key of FILTER_KEYS) values[key] = searchParams.get(key) ?? '';
    if (!searchParams.has('date_from')) values.date_from = defaultFrom();
    return values;
  }, [searchParams]);
  const page = Number(searchParams.get('page') || 1);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    if (!value && key !== 'date_from') next.delete(key);
    next.delete('page');
    setSearchParams(next, { replace: true });
  };
  const setPage = (value: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(value));
    setSearchParams(next, { replace: true });
  };

  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = { page };
    for (const key of FILTER_KEYS) if (filters[key]) params[key] = filters[key];
    return params;
  }, [filters, page]);

  const facetParams = scope === 'platform' && filters.insurer ? { insurer: filters.insurer } : {};
  const { data: facets } = useQuery<Facets>({
    queryKey: ['insurer-activity-facets', facetParams],
    queryFn: async () => (await api.get(`${BASE}facets/`, { params: facetParams })).data,
  });

  const { data, isLoading, isError, error } = useQuery<EventsResponse>({
    queryKey: ['insurer-activity', queryParams],
    queryFn: async () => (await api.get(BASE, { params: queryParams })).data,
  });

  const { data: detail } = useQuery<InsurerAuditEventDetail>({
    queryKey: ['insurer-activity-detail', selected],
    queryFn: async () => (await api.get(`${BASE}${selected}/`)).data,
    enabled: !!selected,
  });

  const canExport = !ownActivityOnly;
  const exportReady = !!filters.date_from || (scope === 'platform' && !!filters.insurer);
  const canVerify = !ownActivityOnly && (scope === 'insurer' || !!filters.insurer);
  const showInsurer = scope === 'platform' && !filters.insurer;

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = { ...queryParams };
      delete params.page;
      const response = await api.get(`${BASE}export/`, { params, responseType: 'blob' });
      const disposition = String(response.headers?.['content-disposition'] ?? '');
      const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'insurer-audit.csv';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getErrorMessage(err) || 'The export failed.');
    } finally {
      setExporting(false);
    }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const params = scope === 'platform' ? { insurer: filters.insurer } : {};
      const res = await api.get(`${BASE}integrity/`, { params });
      if (res.data.ok) {
        toast.success(`Integrity verified: ${res.data.checked} events, chain intact.`);
      } else {
        toast.error(`Integrity check FAILED at sequence ${res.data.first_bad_seq}.`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setVerifying(false);
    }
  };

  const title = scope === 'platform' ? 'Insurer Activity' : ownActivityOnly ? 'My Activity' : 'Activity Log';
  const insurerOptions = [
    ...(user?.role === 'country_admin' || user?.role === 'super_admin'
      ? [{ value: '', label: 'All insurers' }]
      : []),
    ...(facets?.insurers ?? []).map((i) => ({ value: i.id, label: i.name })),
  ];
  const eventOptions = [
    { value: '', label: 'All events' },
    ...(facets?.events ?? []).map((key) => ({ value: key, label: eventLabel(key) })),
  ];

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={
          ownActivityOnly
            ? 'Everything you have done in the portal'
            : 'Who did what, when, and from where. Entries cannot be edited or deleted and are kept for 8 years.'
        }
        breadcrumbs={[{ label: scope === 'platform' ? 'Reports' : 'Insurer' }, { label: title }]}
        actions={
          <div className="flex flex-wrap gap-2">
            {canVerify && (
              <Button variant="outline" size="sm" onClick={verify} disabled={verifying}>
                <ShieldCheckIcon className="w-4 h-4 mr-1" />
                {verifying ? 'Verifying…' : 'Verify integrity'}
              </Button>
            )}
            {canExport && (
              <Button
                variant="outline"
                size="sm"
                onClick={exportCsv}
                disabled={!exportReady || exporting}
                title={exportReady ? 'Up to 500,000 rows per export' : 'Set a start date first'}
              >
                <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                {exporting ? 'Exporting…' : 'Export CSV'}
              </Button>
            )}
          </div>
        }
      />

      {facets?.log_started_at && (
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          Log started on {formatDate(facets.log_started_at)}.
        </p>
      )}

      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {scope === 'platform' && (
            <Select
              aria-label="Insurer"
              options={insurerOptions}
              value={filters.insurer}
              onChange={(e) => setFilter('insurer', e.target.value)}
            />
          )}
          <Input
            placeholder="Search summary or record…"
            value={filters.q}
            leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
            onChange={(e) => setFilter('q', e.target.value)}
          />
          {!ownActivityOnly && (
            <Input
              placeholder="Actor (name or email)"
              value={filters.actor}
              onChange={(e) => setFilter('actor', e.target.value)}
            />
          )}
          <Select
            aria-label="Event"
            options={eventOptions}
            value={filters.event}
            onChange={(e) => setFilter('event', e.target.value)}
          />
          <Select
            aria-label="Outcome"
            options={OUTCOME_OPTIONS}
            value={filters.outcome}
            onChange={(e) => setFilter('outcome', e.target.value)}
          />
          <Input
            type="date"
            aria-label="From date"
            value={filters.date_from}
            onChange={(e) => setFilter('date_from', e.target.value)}
          />
          <Input
            type="date"
            aria-label="To date"
            value={filters.date_to}
            onChange={(e) => setFilter('date_to', e.target.value)}
          />
        </div>
        <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Category">
          {['', ...(facets?.categories ?? Object.keys(CATEGORY_LABELS))].map((key) => (
            <button
              key={key || 'all'}
              type="button"
              onClick={() => setFilter('category', key)}
              aria-pressed={filters.category === key}
              className={`px-3 py-1 rounded-full text-xs font-medium border ${
                filters.category === key
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300'
              }`}
            >
              {key ? CATEGORY_LABELS[key] ?? key : 'All'}
            </button>
          ))}
        </div>
      </Card>

      <div className="mt-6">
        {isLoading ? (
          <ContentLoader />
        ) : isError ? (
          <Card>
            <div className="text-center py-12">
              <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-amber-500" />
              <p className="mt-3 font-medium text-gray-900 dark:text-white">The activity log could not be loaded.</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{getErrorMessage(error)}</p>
            </div>
          </Card>
        ) : !data?.results?.length ? (
          <Card>
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">No activity matches these filters.</div>
          </Card>
        ) : (
          <>
            <div className="space-y-3">
              {data.results.map((row) => (
                <Card key={row.id}>
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => setSelected(row.id)}
                    aria-label={`Details of ${eventLabel(row.event)} #${row.seq}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {eventLabel(row.event)}
                          </span>
                          <Badge variant="info" size="sm">
                            {CATEGORY_LABELS[row.category] ?? row.category}
                          </Badge>
                          <Badge variant={OUTCOME_VARIANT[row.outcome] ?? 'default'} size="sm">
                            {row.outcome}
                          </Badge>
                          {showInsurer && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">{row.insurer_name}</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300 break-words">{row.summary}</p>
                        {row.object_label && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{row.object_label}</p>
                        )}
                      </div>
                      <div className="sm:text-right flex-shrink-0">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{actorLabel(row)}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {row.acting_for_country_admin ? 'Platform administrator' : row.actor_role}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400" title={row.occurred_at}>
                          {formatDateTime(row.occurred_at)}
                        </p>
                      </div>
                    </div>
                  </button>
                </Card>
              ))}
            </div>

            {data.total_pages > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={data.current_page}
                  totalPages={data.total_pages}
                  totalItems={data.count}
                  itemsPerPage={data.page_size}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      <Modal isOpen={!!selected} onClose={() => setSelected(null)} title="Activity details" size="lg">
        {!detail ? (
          <ContentLoader />
        ) : (
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="font-medium text-gray-700 dark:text-gray-300">{eventLabel(detail.event)}</dt>
              <dd className="text-gray-600 dark:text-gray-400">{detail.summary}</dd>
            </div>
            {Object.keys(detail.payload ?? {}).length > 0 && (
              <div>
                <dt className="font-medium text-gray-700 dark:text-gray-300">Details</dt>
                {Object.entries(detail.payload).map(([key, value]) => (
                  <dd key={key} className="text-gray-600 dark:text-gray-400 break-words">
                    {key}: {showValue(value)}
                  </dd>
                ))}
              </div>
            )}
            {Object.keys(detail.changes ?? {}).length > 0 && (
              <div>
                <dt className="font-medium text-gray-700 dark:text-gray-300">Changes</dt>
                {Object.entries(detail.changes).map(([field, change]) => (
                  <dd key={field} className="text-gray-600 dark:text-gray-400 break-words">
                    {field}: {change.changed ? 'changed (value withheld)' : `${showValue(change.old)} → ${showValue(change.new)}`}
                  </dd>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span>When (UTC): {detail.occurred_at}</span>
              <span>Actor: {actorLabel(detail)} {detail.actor_email && `(${detail.actor_email})`}</span>
              <span>IP: {detail.ip_address || '—'}</span>
              <span>Source: {detail.source || '—'}</span>
              <span>Request: {detail.request_id || '—'}</span>
              <span>Sequence: {detail.seq}</span>
              <span className="sm:col-span-2 break-all">Hash: {detail.row_hash}</span>
            </div>
          </dl>
        )}
      </Modal>
    </div>
  );
}
